import useResendProposal from '@/api/mutations/proposals/useResendProposal';
import useSendProposal from '@/api/mutations/proposals/useSendProposal';
import useGetMe from '@/api/queries/me/useGetMe';
import useGetMyAccountBranding from '@/api/queries/me/useGetMyAccountBranding';
import useGetMySendProposalEmailBody from '@/api/queries/me/useGetMySendProposalEmailBody';
import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import {
  useGetProposal,
  useGetProposalEmailPreview,
} from '@/api/queries/proposal/useGetProposal';
import { GetProposalQuery } from '@/gql/graphql';
import { usePermissions } from '@/hooks/usePermissions';
import { useChatAgent } from '@/ui/components/chatAgent/ChatAgentContextProvider';
import BackToEditingButton from '@/ui/components/estimatesAndProposals/draftSendEmail/BackToEditingButton';
import {
  buildPinnedRecipient,
  resolveRecipient,
  sortContacts,
  type Contact,
} from '@/ui/components/estimatesAndProposals/draftSendEmail/contacts';
import DraftSendEmailPreviewFieldset, {
  DRAFT_SEND_EMAIL_PREVIEW_SCHEMA,
  type DraftSendEmailPreviewValues,
} from '@/ui/components/estimatesAndProposals/draftSendEmail/DraftSendEmailPreviewFieldset';
import DraftSendEmailSubmitButton from '@/ui/components/estimatesAndProposals/draftSendEmail/DraftSendEmailSubmitButton';
import { buildDefaultEmailCopy } from '@/ui/components/estimatesAndProposals/draftSendEmail/emailCopy';
import EmailSuppressedAlert from '@/ui/components/estimatesAndProposals/draftSendEmail/EmailSuppressedAlert';
import {
  PanelBody,
  PanelHeaderBar,
} from '@/ui/components/estimatesAndProposals/draftSendEmail/panelLayout';
import { type DraftSendEmailState } from '@/ui/components/estimatesAndProposals/draftSendEmail/types';
import { useBouncedEmailCorrection } from '@/ui/components/estimatesAndProposals/draftSendEmail/useBouncedEmailCorrection';
import { useDraftSendEmailSuppression } from '@/ui/components/estimatesAndProposals/draftSendEmail/useDraftSendEmailSuppression';
import RecipientPicker from '@/ui/components/RecipientPicker';
import { useSenderChoice } from '@/ui/components/senderChoice/useSenderChoice';
import ProposalFinancials from '@/ui/pages/proposalBuilderPage/components/ProposalFinancials';
import { useProposalLifecycleActions } from '@/ui/pages/proposalBuilderPage/components/proposalLifecycleActions/useProposalLifecycleActions';
import ProposalValidForDaysField from '@/ui/pages/proposalBuilderPage/components/ProposalValidForDaysField';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, useToastContext } from '@payaca/ambrosia';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { LabourForecastPermissions } from '@payaca/permissions/labourForecast/labourForecast.permissions';
import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';

type ActionAvailability =
  GetProposalQuery['proposal']['actionAvailability']['send'];

const getSubmitDisabledReason = (params: {
  state: DraftSendEmailState;
  canSend: ActionAvailability | undefined;
  canResend: ActionAvailability | undefined;
  isValidForDaysValid: boolean;
}): string | null => {
  const { state, canSend, canResend, isValidForDaysValid } = params;
  // The valid-for days field only shows on the first send, and a Proposal must
  // carry a validity value before it goes out.
  if (state === 'send' && !isValidForDaysValid) {
    return 'Enter how many days the proposal stays valid before sending.';
  }
  // Each state is gated on its own action: the first send on sendability, a
  // resend on there still being a link to re-send.
  const availability = state === 'send' ? canSend : canResend;
  if (availability && !availability.result) {
    return availability.reasons.join('; ');
  }
  return null;
};

type SendProposalViewProps = {
  /**
   * The way back out of the composer. `to` navigates, and renders an anchor so
   * Ctrl/Cmd+click and open-in-new-tab work; `onClick` is an in-panel view
   * change, for a sent Proposal returning to its actions.
   */
  back: { to: string } | { onClick: () => void; label?: string };
  /** Supply to keep the summary's expanded state across a view change. */
  financials?: { open: boolean; onOpenChange: (open: boolean) => void };
};

/**
 * Drafting the email that carries the Proposal — the first send, a resend, or
 * a resend correcting a bounced address. The twin of `SendEstimateView`.
 */
const SendProposalView: FC<SendProposalViewProps> = (props) => {
  const { back, financials } = props;

  const { projectId, proposalId } = useParams<{
    projectId: string;
    proposalId: string;
  }>();
  const history = useHistory();
  const { pushSuccess } = useToastContext();

  const { data: me, isPending: isLoading } = useGetMe();
  const companyName = me?.user.account.name ?? '';
  const { data: branding } = useGetMyAccountBranding();
  const { project } = useGetProjectByIdOverview(projectId);
  const { proposal } = useGetProposal(proposalId);
  const { emailPreview, reference } = useGetProposalEmailPreview(proposalId);
  const senderChoice = useSenderChoice(me?.emailSenderChoice);

  let state: DraftSendEmailState = 'send';
  if (proposal?.sentAt) {
    state = proposal.bouncedAt ? 'bounced' : 'resend';
  }

  const documentNoun =
    proposal?.documentType === 'ESTIMATE' ? 'estimate' : 'quote';

  const { userHasRequiredPermission } = usePermissions();
  const canSeeMaterialsList = userHasRequiredPermission([
    MaterialsListPermissions.GET_MATERIALS_LISTS,
  ]);
  const canSeeLabourForecast = userHasRequiredPermission([
    LabourForecastPermissions.GET_LABOUR_FORECAST,
  ]);
  const canGetEmailTemplates = userHasRequiredPermission([
    AccountsPermissions.GET_EMAIL_TEMPLATES,
  ]);

  // Remounts the valid-for days field onto the refetched Proposal when Agent
  // Dave changes it. Keyed on the field rather than this pane, which holds an
  // unsent email draft that a remount would discard.
  const { agentWriteRevision } = useChatAgent();

  const [recipientOverride, setRecipientOverride] = useState<Contact | null>(
    null
  );
  // The valid-for days field owns its own autosaving form, so it reports its live
  // validity rather than us reading the (stale until saved) Proposal.
  const [isValidForDaysValid, setIsValidForDaysValid] = useState(true);
  const contacts = sortContacts(project?.customer?.contacts ?? []);
  // Once sent, pin to the stored customerContact so the recipient reflects who
  // actually received it. A Proposal marked as sent out-of-band has no pinned
  // contact, so fall back to the primary Contact, which is who a resend goes to.
  const recipient =
    state === 'send'
      ? resolveRecipient(contacts, recipientOverride)
      : (buildPinnedRecipient(proposal?.customerContact) ??
        resolveRecipient(contacts, null));

  // An Account-level custom HTML email template replaces the editable
  // intro/outro body entirely; the backend renders it on send.
  const customEmailTemplate = canGetEmailTemplates
    ? proposal?.emailTemplate
    : undefined;

  const { emailCopy: emailCopyTemplate } = useGetMySendProposalEmailBody();

  const { intro: defaultIntro, outro: defaultOutro } = buildDefaultEmailCopy({
    template: emailCopyTemplate,
    recipientName: recipient?.name,
    reference: String(reference ?? ''),
    companyName,
    documentNoun,
  });

  // With a custom template the backend ignores intro/outro, so omit them.
  const buildEmailContent = (values: DraftSendEmailPreviewValues) =>
    customEmailTemplate
      ? undefined
      : {
          intro: values.intro.trim() || undefined,
          outro: values.outro.trim() || undefined,
        };

  const sendProposal = useSendProposal({ proposalId, projectId });
  const resendProposal = useResendProposal({ proposalId, projectId });

  const lifecycle = useProposalLifecycleActions({ proposalId, projectId });

  const goToProjectProposals = () =>
    history.push(`/projects/${projectId}/proposals`);

  const finaliseResend = (values: DraftSendEmailPreviewValues) => {
    resendProposal.mutate(
      {
        id: proposalId,
        emailContent: buildEmailContent(values),
      },
      {
        onSuccess: () => {
          pushSuccess(`Proposal resent to ${recipient?.name ?? 'customer'}.`);
          goToProjectProposals();
        },
      }
    );
  };

  const { submitResend, isPending: isCorrectingEmail } =
    useBouncedEmailCorrection({ state, recipient, onResend: finaliseResend });

  // Mark-as-sent is fired from the submit button's own menu rather than this
  // form, but it transitions the same Proposal — leaving the send button live
  // while it is in flight would email the Customer off a stale sendability
  // check.
  const isSubmitting =
    sendProposal.isPending ||
    resendProposal.isPending ||
    lifecycle.isMarkingAsSent ||
    isCorrectingEmail;

  const formMethods = useForm<DraftSendEmailPreviewValues>({
    defaultValues: {
      intro: '',
      outro: '',
      recipientEmail: '',
    },
    resolver: zodResolver(DRAFT_SEND_EMAIL_PREVIEW_SCHEMA),
  });

  const suppression = useDraftSendEmailSuppression({
    state,
    recipient,
    control: formMethods.control,
  });

  const handleSend = (values: DraftSendEmailPreviewValues) => {
    if (!recipient?.email) return;
    sendProposal.mutate(
      {
        id: proposalId,
        recipientCustomerContactId: recipient.publicId,
        emailContent: buildEmailContent(values),
      },
      {
        onSuccess: () => {
          pushSuccess(`Proposal sent to ${recipient.name ?? 'customer'}.`);
          goToProjectProposals();
        },
      }
    );
  };

  const handleSubmit = (values: DraftSendEmailPreviewValues) => {
    if (isSubmitting) return;
    if (state === 'send') handleSend(values);
    else submitResend(values);
  };

  return (
    <Form
      methods={formMethods}
      onSubmit={handleSubmit}
      className="flex h-full min-h-0 flex-col"
    >
      <PanelHeaderBar>
        <PanelHeaderBar.Group className="min-w-0">
          {/* No reference or status here: whichever view sits behind the back
              link already carries them. */}
          <BackToEditingButton {...back} disabled={isSubmitting} />
        </PanelHeaderBar.Group>

        <PanelHeaderBar.Actions>
          <RecipientPicker
            recipient={recipient}
            contacts={contacts}
            onSelectRecipient={
              state === 'send' ? setRecipientOverride : undefined
            }
          />

          <DraftSendEmailSubmitButton
            state={state}
            entityLabel="proposal"
            recipient={recipient}
            isLoading={isLoading}
            isSubmitting={isSubmitting}
            isSuppressed={suppression.isSuppressed}
            submitDisabledReason={getSubmitDisabledReason({
              state,
              canSend: proposal?.actionAvailability.send,
              canResend: proposal?.actionAvailability.resend,
              isValidForDaysValid,
            })}
            // Once sent, the panel's actions list carries these instead, so
            // the resend button carries none.
            additionalActions={state === 'send' ? lifecycle.actions : undefined}
            additionalActionsLabel="Update this proposal"
          />
        </PanelHeaderBar.Actions>
      </PanelHeaderBar>

      <PanelBody>
        <ProposalFinancials
          proposalId={proposalId}
          projectId={projectId}
          editMaterialsTo={
            canSeeMaterialsList ? `/projects/${projectId}/materials` : undefined
          }
          editLabourTo={
            canSeeLabourForecast ? `/projects/${projectId}/labour` : undefined
          }
          defaultOpen={financials?.open}
          onOpenChange={financials?.onOpenChange}
        />

        <PanelBody.Content>
          <EmailSuppressedAlert
            entityLabel="proposal"
            suppression={suppression}
          />

          {/* Only a draft's valid-for days can be changed — once sent, the
                expiry is locked in, mirroring the backend mutation's gate. */}
          {state === 'send' && proposal && (
            <ProposalValidForDaysField
              key={agentWriteRevision}
              proposalId={proposalId}
              initialValidForDays={proposal.validForDays ?? null}
              onValidityChange={setIsValidForDaysValid}
            />
          )}

          <DraftSendEmailPreviewFieldset
            isLoading={isLoading}
            recipient={recipient}
            editableRecipient={state === 'bounced'}
            documentNoun={documentNoun}
            logoUrl={branding?.logoUrl}
            senderChoice={senderChoice}
            subject={emailPreview?.subject}
            defaultIntro={customEmailTemplate ? undefined : defaultIntro}
            defaultOutro={customEmailTemplate ? undefined : defaultOutro}
            customEmailMarkup={customEmailTemplate?.preview}
          />
        </PanelBody.Content>
      </PanelBody>
    </Form>
  );
};

export default SendProposalView;
